import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';

import { v4 as uuidv4 } from 'uuid';

import { Dictionary } from '@reduxjs/toolkit';

import { RootState } from '../../../../app/rootReducer';
import { getReasonsEndPoint } from '../../../../common/api.endPoints';
import { Reason, ReasonsTransform } from '../../../../common/constants/reasons';
import { HttpService } from '../../../../common/http.service';
import { SnackbarContext } from '../../../layout/SnackbarContext';
import { ITask } from '../../../tasks/interfaces/ITask';
import { AdditionalCustomerInfo } from '../../../tasks/types/additionalCustomerInfo';
import { AdditionalDocuments } from '../../../tasks/types/additionalDocuments';
import { Aps } from '../../../tasks/types/aps';
import { AutoMedicalClaims } from '../../../tasks/types/autoMedicalClaims';
import { CriminalRecords } from '../../../tasks/types/criminalRecords';
import { Dispute } from '../../../tasks/types/dispute';
import { EHR } from '../../../tasks/types/ehr';
import { Eti } from '../../../tasks/types/eti';
import { InsuranceLabs } from '../../../tasks/types/insuranceLabs';
import { LabPiqture } from '../../../tasks/types/labPiqture';
import { MedExam } from '../../../tasks/types/medExam';
import { MedicalClaims } from '../../../tasks/types/medicalClaims';
import { MibCodeDetails } from '../../../tasks/types/mibCodeDetails';
import { PlanF } from '../../../tasks/types/planF';
import { ScriptCheck } from '../../../tasks/types/scriptCheck';
import { SpecialAuthorization } from '../../../tasks/types/specialAuthorization';
import { TRLOrder } from '../../../tasks/types/TRLOrder';
import { IExpandedState } from '../../interfaces/IExpandedState';
import { OrderEvidence } from './OrderEvidence';
import { ReviewEvidence } from './ReviewEvidence';

const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export const EvidenceDrawer = ({
  isOpen,
  setIsOpen,
  applicationId,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  applicationId: string;
}) => {
  const {
    orderEvidenceFailed,
    orderEvidenceError,
    orderEvidenceErrorType,
    product,
    carrier,
    subCategory,
    existingTasks,
  }: {
    orderEvidenceFailed: boolean;
    orderEvidenceError: string;
    orderEvidenceErrorType: string;
    product: string;
    carrier: string;
    subCategory: string;
    existingTasks: Dictionary<ITask>;
  } = useSelector((state: RootState) => {
    return {
      orderEvidenceFailed: state.taskUi.orderEvidenceFailed,
      orderEvidenceError: state.taskUi.orderEvidenceError,
      orderEvidenceErrorType: state.taskUi.orderEvidenceErrorType,
      product: state.application.product,
      carrier: state.application.carrier,
      subCategory: state.application.subCategory,
      existingTasks: state.taskList.entities,
    };
  });

  const [isReview, setIsReview] = useState(false);
  const [medicalExam, setMedicalExam] = useState({} as MedExam);
  const [eti, setEti] = useState({} as Eti);
  const [aps, setAps] = useState({} as Aps);
  const [ehr, setEhr] = useState({} as EHR);
  const [labPiqture, setLabPiqture] = useState({} as LabPiqture);
  const [medicalClaims, setMedicalClaims] = useState({} as MedicalClaims);
  const [autoMedicalClaims, setAutoMedicalClaims] = useState(
    {} as AutoMedicalClaims,
  );
  const [scriptCheck, setScriptCheck] = useState({} as ScriptCheck);
  const [mibCodeDetails, setMibCodeDetails] = useState({} as MibCodeDetails);
  const [insuranceLabs, setInsuranceLabs] = useState({} as InsuranceLabs);
  const [criminalRecords, setCriminalRecords] = useState({} as CriminalRecords);
  const [additionalCustomerInfo, setAdditionalCustomerInfo] = useState(
    {} as AdditionalCustomerInfo,
  );
  const [specialAuthorization, setSpecialAuthorization] = useState(
    {} as SpecialAuthorization,
  );
  const [planF, setPlanF] = useState({} as PlanF);
  const [trlOrder, setTRLOrder] = useState({} as TRLOrder);
  const [dispute, setDispute] = useState({} as Dispute);
  const [reasons, setReasons] = useState({} as Reason[]);
  const [expanded, setExpanded] = useState({} as IExpandedState);
  const [additionalDocuments, setAdditionalDocuments] = useState(
    {} as AdditionalDocuments,
  );

  useEffect(() => {
    if (isOpen) {
      (async () => {
        const response = await HttpService.getInstance().get(
          getReasonsEndPoint(applicationId),
        );

        if (response && !response.error) {
          const orderReason: [] = response.parsedBody;
          const orderReasonMap: Reason[] = orderReason.map(item => {
            return ReasonsTransform.getReason(item) as Reason;
          });
          setReasons(orderReasonMap as Reason[]);
        }
      })();
    }
  }, [isOpen]);

  useEffect(() => {
    setExpanded({} as IExpandedState);
  }, [isOpen]);

  // XHR error handling
  const { openSnacks, setSnacks } = useContext(SnackbarContext);
  const updateSnacks = (updatedSnacks: any) => {
    setSnacks(updatedSnacks);
  };

  useEffect(() => {
    if (orderEvidenceFailed) {
      setSnacks([
        ...openSnacks,
        {
          id: uuidv4(),
          type: orderEvidenceErrorType,
          message: orderEvidenceError,
        },
      ]);
    }
  }, [orderEvidenceFailed, orderEvidenceError, orderEvidenceErrorType]);

  return (
    <>
      {isReview && (
        <ReviewEvidence
          applicationId={applicationId}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          setIsReview={setIsReview}
          medicalExam={medicalExam}
          eti={eti}
          aps={aps}
          ehr={ehr}
          labPiqture={labPiqture}
          medicalClaims={medicalClaims}
          autoMedicalClaims={autoMedicalClaims}
          trlOrder={trlOrder}
          scriptCheck={scriptCheck}
          mibCodeDetails={mibCodeDetails}
          insuranceLabs={insuranceLabs}
          criminalRecords={criminalRecords}
          openSnacks={openSnacks}
          updateSnacks={updateSnacks}
          additionalCustomerInfo={additionalCustomerInfo}
          specialAuthorization={specialAuthorization}
          planF={planF}
          dispute={dispute}
          additionalDocuments={additionalDocuments}
        />
      )}
      {!isReview && (
        <OrderEvidence
          product={product}
          carrier={carrier}
          subCategory={subCategory}
          existingTasks={existingTasks}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          setIsReview={setIsReview}
          reasons={reasons}
          expanded={expanded}
          setExpanded={setExpanded}
          setMedicalExam={setMedicalExam}
          medicalExam={medicalExam}
          setEti={setEti}
          eti={eti}
          setAps={setAps}
          aps={aps}
          ehr={ehr}
          setEhr={setEhr}
          labPiqture={labPiqture}
          setLabPiqture={setLabPiqture}
          medicalClaims={medicalClaims}
          setMedicalClaims={setMedicalClaims}
          autoMedicalClaims={autoMedicalClaims}
          setAutoMedicalClaims={setAutoMedicalClaims}
          trlOrder={trlOrder}
          setTRLOrder={setTRLOrder}
          scriptCheck={scriptCheck}
          setScriptCheck={setScriptCheck}
          mibCodeDetails={mibCodeDetails}
          setMibCodeDetails={setMibCodeDetails}
          insuranceLabs={insuranceLabs}
          setInsuranceLabs={setInsuranceLabs}
          criminalRecords={criminalRecords}
          setCriminalRecords={setCriminalRecords}
          additionalCustomerInfo={additionalCustomerInfo}
          setAdditionalCustomerInfo={setAdditionalCustomerInfo}
          specialAuthorization={specialAuthorization}
          setSpecialAuthorization={setSpecialAuthorization}
          planF={planF}
          setPlanF={setPlanF}
          dispute={dispute}
          setDispute={setDispute}
          additionalDocuments={additionalDocuments}
          setAdditionalDocuments={setAdditionalDocuments}
        />
      )}
    </>
  );
};
